import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, ArrowRight, Sparkles, Crown, Loader2, Zap, FileText, Download, Palette, ShoppingBag, XCircle, Package, Shield, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { formatPrice, detectCurrency } from '../data/pricing';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CrossSellCard = ({ product, currency, onBuy }) => {
  const price = currency === 'nok' ? product.price_nok : product.price_usd;
  return (
    <div
      data-testid={`cross-sell-${product.slug}`}
      onClick={onBuy}
      className="bg-white rounded-xl border border-stone-100 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group"
    >
      <p className="font-sans font-semibold text-stone-800 text-sm mb-1">{product.title}</p>
      <p className="font-sans text-[11px] text-stone-400 mb-2">{product.type} &middot; {product.page_count}</p>
      <div className="flex items-center justify-between">
        <span className="text-sm font-serif font-bold text-stone-900">{formatPrice(price, currency)}</span>
        <ArrowRight className="w-3.5 h-3.5 text-stone-300 group-hover:text-stone-600 transition-colors" />
      </div>
    </div>
  );
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser, fetchPurchases } = useAuth();

  const purchaseType = searchParams.get('type'); // 'product', 'bundle', or null (subscription)
  const provider = searchParams.get('provider');
  const sessionId = searchParams.get('session_id');
  const paypalToken = searchParams.get('token');
  const productSlug = searchParams.get('product');
  const bundleSlug = searchParams.get('bundle');

  const [status, setStatus] = useState('verifying');
  const [productInfo, setProductInfo] = useState(null);
  const [crossSellProducts, setCrossSellProducts] = useState([]);
  const [countdown, setCountdown] = useState(15);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);
  const capturedRef = useRef(false);
  const currency = detectCurrency();

  const isProductPurchase = purchaseType === 'product';
  const isBundlePurchase = purchaseType === 'bundle';

  // ── PayPal capture (Product) ──
  useEffect(() => {
    if (!isProductPurchase || provider !== 'paypal' || !paypalToken || capturedRef.current) return;
    capturedRef.current = true;

    const capturePayPal = async () => {
      try {
        const res = await axios.post(`${API}/purchases/paypal/capture/${paypalToken}`);
        if (res.data.status === 'COMPLETED') {
          setStatus('success');
          setProductInfo({ slug: res.data.product_slug, title: res.data.product_title });
          if (fetchPurchases) await fetchPurchases();
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('PayPal product capture error:', err);
        setStatus('error');
      }
    };
    capturePayPal();
  }, [isProductPurchase, provider, paypalToken, fetchPurchases]);

  // ── PayPal capture (Pro subscription — new subscription flow) ──
  useEffect(() => {
    if (isProductPurchase) return;
    if (provider !== 'paypal' || capturedRef.current) return;
    capturedRef.current = true;

    // PayPal subscriptions return subscription_id in URL params
    const subscriptionId = searchParams.get('subscription_id') || paypalToken;
    if (!subscriptionId) { setStatus('error'); return; }

    const activateSubscription = async () => {
      try {
        const res = await axios.post(`${API}/paypal/activate-subscription/${subscriptionId}`);
        if (res.data.status === 'ACTIVE') {
          setStatus('success');
          if (refreshUser) await refreshUser();
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('PayPal subscription activation error:', err);
        setStatus('error');
      }
    };
    activateSubscription();
  }, [isProductPurchase, provider, paypalToken, refreshUser, searchParams]);

  // ── Fallback: if no polling mechanism applies, show error (no unverified success) ──
  useEffect(() => {
    if (status !== 'verifying') return;
    const isPayPalProduct = isProductPurchase && provider === 'paypal' && paypalToken;
    const isPayPalSub = !isProductPurchase && !isBundlePurchase && provider === 'paypal';
    const isStripeProduct = isProductPurchase && provider !== 'paypal' && sessionId;
    const isStripeBundle = isBundlePurchase && provider !== 'paypal' && sessionId;
    const isStripeSub = !isProductPurchase && !isBundlePurchase && provider !== 'paypal' && sessionId;

    if (!isPayPalProduct && !isPayPalSub && !isStripeProduct && !isStripeBundle && !isStripeSub) {
      // No session_id or paypal token — cannot verify payment, show pending
      setStatus('pending');
      if (productSlug) setProductInfo({ slug: productSlug, title: '' });
      else if (bundleSlug) setProductInfo({ slug: bundleSlug, title: '' });
    }
  }, [status, isProductPurchase, isBundlePurchase, provider, sessionId, paypalToken, productSlug, bundleSlug]);

  // ── Stripe polling (Product) ──
  useEffect(() => {
    if (!isProductPurchase || provider === 'paypal' || !sessionId) return;

    let attempts = 0;
    const maxAttempts = 20;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await axios.get(`${API}/purchases/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          clearInterval(pollRef.current);
          setStatus('success');
          setProductInfo({ slug: res.data.product_slug, title: res.data.product_title });
          if (fetchPurchases) await fetchPurchases();
        } else if (attempts >= maxAttempts) {
          clearInterval(pollRef.current);
          setStatus('pending');
          setProductInfo({ slug: productSlug, title: '' });
          if (fetchPurchases) await fetchPurchases();
        }
      } catch {
        if (attempts >= maxAttempts) {
          clearInterval(pollRef.current);
          setStatus('pending');
          setProductInfo({ slug: productSlug, title: '' });
        }
      }
    }, 2000);

    return () => clearInterval(pollRef.current);
  }, [isProductPurchase, sessionId, provider, fetchPurchases, productSlug]);

  // ── Stripe polling (Bundle) ──
  useEffect(() => {
    if (!isBundlePurchase || provider === 'paypal' || !sessionId) return;

    let attempts = 0;
    const maxAttempts = 20;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await axios.get(`${API}/purchases/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          clearInterval(pollRef.current);
          setStatus('success');
          setProductInfo({ slug: res.data.product_slug, title: res.data.product_title });
          if (fetchPurchases) await fetchPurchases();
        } else if (attempts >= maxAttempts) {
          clearInterval(pollRef.current);
          setStatus('pending');
          setProductInfo({ slug: bundleSlug, title: '' });
          if (fetchPurchases) await fetchPurchases();
        }
      } catch {
        if (attempts >= maxAttempts) {
          clearInterval(pollRef.current);
          setStatus('pending');
          setProductInfo({ slug: bundleSlug, title: '' });
        }
      }
    }, 2000);

    return () => clearInterval(pollRef.current);
  }, [isBundlePurchase, sessionId, provider, fetchPurchases, bundleSlug]);

  // ── Stripe polling (Pro subscription — existing) ──
  useEffect(() => {
    if (isProductPurchase || provider === 'paypal') return;
    if (!sessionId) { setStatus('error'); return; }

    let attempts = 0;
    const maxAttempts = 20;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          clearInterval(pollRef.current);
          setStatus('success');
          if (refreshUser) await refreshUser();
        } else if (attempts >= maxAttempts) {
          clearInterval(pollRef.current);
          setStatus('pending');
          if (refreshUser) await refreshUser();
        }
      } catch {
        if (attempts >= maxAttempts) {
          clearInterval(pollRef.current);
          setStatus('pending');
        }
      }
    }, 2000);

    return () => clearInterval(pollRef.current);
  }, [isProductPurchase, sessionId, refreshUser, provider]);

  // ── Fetch cross-sell recommendations after success ──
  useEffect(() => {
    if (status !== 'success') return;
    const slug = productSlug || productInfo?.slug || bundleSlug;
    if (!slug) return;

    const fetchCrossSell = async () => {
      try {
        const res = await axios.get(`${API}/purchases/cross-sell/${slug}`);
        setCrossSellProducts(res.data.product_recommendations || []);
      } catch (err) {
        // Silent — cross-sell is optional
      }
    };
    fetchCrossSell();
  }, [status, productSlug, productInfo, bundleSlug]);

  // ── Auto-redirect countdown ──
  useEffect(() => {
    if (status !== 'success') return;
    const target = isProductPurchase
      ? `/generator?product=${productSlug || productInfo?.slug || ''}`
      : isBundlePurchase
        ? '/dashboard?tab=purchases'
        : '/dashboard';
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownRef.current); navigate(target); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [status, navigate, isProductPurchase, isBundlePurchase, productSlug, productInfo]);

  // ── Show toast for product/bundle purchase ──
  const toastShownRef = useRef(false);
  useEffect(() => {
    if (status !== 'success' || toastShownRef.current) return;
    if (!isProductPurchase && !isBundlePurchase) return;
    toastShownRef.current = true;
    const displayTitle = isBundlePurchase
      ? (productInfo?.title || bundleSlug?.replace(/-/g, ' ') || 'Your collection')
      : (productInfo?.title || productSlug?.replace(/-/g, ' ') || 'Your product');
    setTimeout(() => {
      toast.success(isBundlePurchase ? 'Your collection is ready!' : 'Your product is ready!', {
        description: isBundlePurchase
          ? `${displayTitle} — all products are now available.`
          : `${displayTitle} is now available in My Purchases.`,
        duration: 6000,
      });
    }, 300);
  }, [status, isProductPurchase, isBundlePurchase, productInfo, bundleSlug, productSlug]);

  // ── Error state ──
  if (status === 'error') {
    return (
      <div data-testid="payment-error" className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-stone-800 mb-2">Something went wrong</h1>
          <p className="text-stone-500 font-sans text-sm mb-6">
            We could not confirm the payment. No charge was made. Please try again or contact support.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/')} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-5 font-sans font-semibold text-sm">
              Back to home
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="rounded-full px-6 py-5 font-sans font-semibold text-sm border-stone-200">
              Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Pending state — payment still processing ──
  if (status === 'pending') {
    return (
      <div data-testid="payment-pending" className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-stone-800 mb-2">Payment is being processed</h1>
          <p className="text-stone-500 font-sans text-sm mb-6">
            Your payment is still being confirmed. This usually takes just a moment. Please check your email for a confirmation, or visit your dashboard.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/dashboard')} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-5 font-sans font-semibold text-sm">
              Go to Dashboard
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()} className="rounded-full px-6 py-5 font-sans font-semibold text-sm border-stone-200">
              Refresh
            </Button>
          </div>
        </div>
      </div>
    );
  }


  // ── Verifying state ──
  if (status === 'verifying') {
    return (
      <div data-testid="payment-verifying" className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-serif font-bold text-stone-800 mb-2">Verifying payment...</h1>
          <p className="text-stone-500 font-sans text-sm">
            {isProductPurchase
              ? 'Confirming your purchase. This only takes a moment.'
              : provider === 'paypal'
                ? 'Confirming your PayPal payment. This only takes a moment.'
                : 'Confirming your payment with Stripe. This only takes a moment.'}
          </p>
        </div>
      </div>
    );
  }

  // ── Product Purchase Success ──
  if (isProductPurchase || isBundlePurchase) {
    const title = isBundlePurchase
      ? (productInfo?.title || bundleSlug?.replace(/-/g, ' ') || 'Your collection')
      : (productInfo?.title || productSlug?.replace(/-/g, ' ') || 'Your product');

    const purchasedLicenseType = searchParams.get('license_type') || 'personal';
    const isCommercialLicense = purchasedLicenseType === 'commercial' || isBundlePurchase;

    return (
      <div data-testid={isBundlePurchase ? "bundle-purchase-success" : "product-purchase-success"} className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center px-6 py-12 relative overflow-hidden">
        <div className="relative z-10 max-w-lg w-full">
          <div className="bg-white rounded-2xl shadow-[0_8px_60px_rgba(0,0,0,0.08)] border border-stone-100/60 overflow-hidden">
            <div className="bg-[#111111] px-8 py-6 text-center">
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-1.5 rounded-full mb-3">
                {isBundlePurchase ? <Package className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />}
                <span className="text-xs font-sans font-bold tracking-wide uppercase">
                  {isBundlePurchase ? 'Collection purchased' : 'Purchase complete'}
                </span>
              </div>
              <h1 className="text-3xl font-serif font-bold text-white tracking-tight capitalize">{title}</h1>
            </div>
            <div className="px-8 py-8">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                </div>
              </div>
              <p className="text-stone-600 font-sans text-center text-sm leading-relaxed mb-4">
                {isBundlePurchase
                  ? <>Payment confirmed! All products in <strong className="text-stone-800 capitalize">{title}</strong> are now available. Find them under &ldquo;My Purchases&rdquo; in your dashboard.</>
                  : <>Payment confirmed! You now have permanent access to <strong className="text-stone-800 capitalize">{title}</strong>. Find it under &ldquo;My Purchases&rdquo; in your dashboard.</>}
              </p>

              {/* License Type Confirmation */}
              <div data-testid="license-confirmation" className="bg-[#F7F2EC] border border-[#EDE3D6] rounded-xl p-4 mb-6 text-center">
                {isCommercialLicense ? (
                  <>
                    <div className="flex items-center justify-center gap-2 mb-1.5">
                      <Shield className="w-4 h-4 text-[#C4A98D]" />
                      <span className="text-sm font-semibold text-[#4A3525]">Commercial License</span>
                    </div>
                    <p className="text-[12px] text-[#8A7D72] mb-3">
                      Commercial rights included. Customize it, sell it and build your business.
                    </p>
                    <button
                      data-testid="download-license-btn"
                      onClick={() => navigate('/dashboard?tab=licenses')}
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#8C7A66] hover:text-[#4A3525] transition-colors"
                    >
                      <FileText className="w-3 h-3" />Download Commercial License
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-2 mb-1.5">
                      <CheckCircle2 className="w-4 h-4 text-stone-400" />
                      <span className="text-sm font-semibold text-[#4A3525]">Personal Use License</span>
                    </div>
                    <p className="text-[12px] text-[#8A7D72]">
                      For your own personal use. Commercial use and resale are not included.
                    </p>
                  </>
                )}
              </div>

              {/* Cross-sell recommendations */}
              {crossSellProducts.length > 0 && (
                <div className="mb-6">
                  <p className="text-[10px] font-sans font-bold uppercase tracking-widest text-stone-400 mb-3">You might also like</p>
                  <div className="space-y-2">
                    {crossSellProducts.map(product => (
                      <CrossSellCard
                        key={product.slug}
                        product={product}
                        currency={currency}
                        onBuy={() => {
                          clearInterval(countdownRef.current);
                          navigate(`/?buy=${product.slug}`);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <Button
                data-testid="product-success-open-btn"
                onClick={() => { clearInterval(countdownRef.current); navigate(isBundlePurchase ? '/dashboard?tab=purchases' : `/generator?product=${productSlug || productInfo?.slug || ''}`); }}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-2xl py-6 font-sans font-bold text-sm shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 mb-3"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {isBundlePurchase ? 'View your products' : 'Continue where you left off'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                data-testid="product-success-dashboard-btn"
                variant="ghost"
                onClick={() => { clearInterval(countdownRef.current); navigate('/dashboard?tab=purchases'); }}
                className="w-full text-stone-500 font-sans text-sm"
              >
                <ShoppingBag className="w-4 h-4 mr-2" /> Go to My Purchases
              </Button>
              <p className="text-center text-[11px] font-sans text-stone-400 mt-4">
                Redirecting in {countdown} seconds...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Pro Subscription Success (existing) ──
  const proFeatures = [
    { icon: Zap, text: 'Unlimited AI generation' },
    { icon: Palette, text: 'All 32+ premium templates' },
    { icon: Download, text: 'PDF & PNG export' },
    { icon: Shield, text: 'Commercial License included' },
    { icon: FileText, text: 'Pinterest Pin Creator' },
  ];

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center px-6 relative overflow-hidden">
      <div className="relative z-10 max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-[0_8px_60px_rgba(0,0,0,0.08)] border border-stone-100/60 overflow-hidden">
          <div className="bg-[#111111] px-8 py-6 text-center">
            <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-1.5 rounded-full mb-3">
              <Crown className="w-4 h-4" />
              <span className="text-xs font-sans font-bold tracking-wide uppercase">Pro Plan Active</span>
            </div>
            <h1 className="text-3xl font-serif font-bold text-white tracking-tight">Welcome to Pro!</h1>
          </div>
          <div className="px-8 py-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center animate-bounce-once">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
            </div>
            <p className="text-stone-600 font-sans text-center text-sm leading-relaxed mb-6">
              Payment confirmed{provider === 'paypal' ? ' via PayPal' : ''}! You now have access to all Pro features. Start creating your first premium product.
            </p>
            <div className="bg-stone-50 rounded-2xl p-5 mb-6">
              <p className="text-xs font-sans font-semibold text-stone-500 uppercase tracking-wider mb-3">Your Pro features</p>
              <div className="grid grid-cols-2 gap-3">
                {proFeatures.map(f => (
                  <div key={f.text} className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm">
                      <f.icon className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-xs font-sans font-medium text-stone-700">{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <Button
              data-testid="success-create-btn"
              onClick={() => { clearInterval(countdownRef.current); navigate('/generator'); }}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-2xl py-6 font-sans font-bold text-sm shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 mb-3"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Start creating now <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              data-testid="success-dashboard-btn"
              variant="ghost"
              onClick={() => { clearInterval(countdownRef.current); navigate('/dashboard'); }}
              className="w-full text-stone-500 font-sans text-sm"
            >
              Go to Dashboard
            </Button>
            <p className="text-center text-[11px] font-sans text-stone-400 mt-4">
              Redirecting to dashboard in {countdown} seconds...
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
